import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system/styleFunctionSx';

interface ISideNavOwnerState {
  active: boolean;
  transparentSidenav: boolean;
}

interface ICollapseItemOwnerState extends ISideNavOwnerState {
  darkMode: boolean;
  whiteSidenav: boolean;
}

interface ICollapseTextOwnerState extends ISideNavOwnerState {
  miniSidenav: boolean;
}

function collapseItem(theme: Theme, ownerState: ICollapseItemOwnerState): SystemStyleObject<Theme> {
  const { palette, transitions, breakpoints, borders, functions } = theme;
  const { active, transparentSidenav, whiteSidenav, darkMode } = ownerState;

  const { white, transparent, dark } = palette;
  const { borderRadius } = borders;
  const { pxToRem, rgba } = functions;

  const inactiveColor =
    (transparentSidenav && !darkMode) || whiteSidenav ? dark.main : white.main;

  return {
    background: active ? '#e6ff55' : transparent.main,
    color: active ? '#0c231a' : inactiveColor,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${pxToRem(8)} ${pxToRem(10)}`,
    margin: `${pxToRem(1.5)} ${pxToRem(16)}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    [breakpoints.up('xl')]: {
      transition: transitions.create(['box-shadow', 'background-color'], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.shorter
      })
    },

    '&:hover, &:focus': {
      backgroundColor: active ? '#e6ff55' : rgba(white.main, 0.08)
    }
  };
}

function collapseIconBox(
  theme: Theme,
  ownerState: ICollapseItemOwnerState
): SystemStyleObject<Theme> {
  const { palette, transitions, borders, functions } = theme;
  const { transparentSidenav, whiteSidenav, darkMode, active } = ownerState;

  const { white, dark } = palette;
  const { borderRadius } = borders;
  const { pxToRem } = functions;

  let iconColor: string;
  if ((transparentSidenav && !darkMode && !active) || (whiteSidenav && !active)) {
    iconColor = dark.main;
  } else if (active) {
    iconColor = '#0c231a';
  } else {
    iconColor = white.main;
  }

  let svgColor: string;
  if (transparentSidenav || whiteSidenav) {
    svgColor = dark.main;
  } else if (active) {
    svgColor = '#0c231a';
  } else {
    svgColor = white.main;
  }

  return {
    minWidth: pxToRem(32),
    minHeight: pxToRem(32),
    color: iconColor,
    borderRadius: borderRadius.md,
    display: 'grid',
    placeItems: 'center',
    transition: transitions.create('margin', {
      easing: transitions.easing.easeInOut,
      duration: transitions.duration.standard
    }),

    '& svg, svg g': {
      color: svgColor
    }
  };
}

const collapseIcon = (_theme: Theme, { active }: { active: boolean }) => ({
  color: active ? '#0c231a' : '#ffffff'
});

function collapseText(theme: Theme, ownerState: ICollapseTextOwnerState): SystemStyleObject<Theme> {
  const { typography, transitions, breakpoints, functions } = theme;
  const { miniSidenav, transparentSidenav, active } = ownerState;

  const { size, fontWeightRegular, fontWeightLight } = typography;
  const { pxToRem } = functions;

  return {
    marginLeft: pxToRem(10),

    [breakpoints.up('xl')]: {
      opacity: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : 1,
      maxWidth: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : '100%',
      marginLeft: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : pxToRem(10),
      transition: transitions.create(['opacity', 'margin'], {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard
      })
    },

    '& span': {
      fontWeight: active ? fontWeightRegular : fontWeightLight,
      fontSize: size.sm,
      lineHeight: 0
    }
  };
}

export { collapseItem, collapseIconBox, collapseIcon, collapseText };
